import { useState, useCallback, useMemo, useEffect, useContext } from "react";
import { useFormatDescription } from "src/utils/useFormatDescription.js";
import { useIsEventLive } from "src/utils/useIsEventLive.js";
import { useUpcomingEventStatus } from "src/utils/useUpcomingEventStatus.js";
import { getMinuteTimestamp } from "./Utils.js";
import { AuthContext } from "./AuthContext.js";
import { useGetCalendarsByHandle } from "src/services/index.js";

/*
    Here we are creating a higher order component that will be used to compose the event data and UI
    for the Event component and the RecommendedCalendar component.
*/

const UIComponent = ({ Component, ...props }) => {
  const { user } = useContext(AuthContext);
  const {
    event,
    calendar,
    setAddedCalendars,
    onSubscribeAction = null,
    eventIdSubscribed,
    position,
    isPastEvent,
    isEmbedded,
    type,
    setShowRecommendedCalendars,
  } = props;
  const { jCal } = event;
  const eventId = useMemo(
    () => jCal?.[1]?.find((prop) => prop[0] === "uid")[3],
    [jCal]
  );
  const eventSummary = useMemo(
    () => jCal?.[1]?.find((prop) => prop[0] === "summary"),
    [jCal]
  );
  const eventDateStart = useMemo(
    () => jCal?.[1]?.find((prop) => prop[0] === "dtstart"),
    [jCal]
  );
  const eventDateEnd = useMemo(
    () =>
      jCal?.[1]?.find((prop) => prop[0] === "dtend") ||
      jCal?.[1]?.find((prop) => prop[0] === "dtstart"),
    [jCal]
  ); // In cases where events start time / end time is the same, the dtend property is missing. Default to dtstart.
  const eventLocation = useMemo(
    () => jCal?.[1]?.find((prop) => prop[0] === "location"),
    [jCal]
  );
  const eventDescription = useMemo(
    () => jCal?.[1]?.find((prop) => prop[0] === "description")?.[3],
    [jCal]
  );
  const homeTeamLogo = useMemo(
    () => jCal?.[1]?.find((prop) => prop[0] === "x-homelogourl")?.[3],
    [jCal]
  );
  const awayTeamLogo = useMemo(
    () => jCal?.[1]?.find((prop) => prop[0] === "x-awaylogourl")?.[3],
    [jCal]
  );
  const background = useMemo(
    () => jCal?.[1]?.find((prop) => prop[0] === "x-backgroundurl")?.[3],
    [jCal]
  );
  const eventTvSeasonEpisode = useMemo(
    () => /S(\d+)\sE(\d+)/.exec(eventLocation)?.[0] ?? false,
    [eventLocation]
  );
  const isEventLive = useIsEventLive(eventDateStart, eventDateEnd);
  const upcomingEventStatus = useUpcomingEventStatus(
    eventDateStart,
    eventDateEnd
  );

  const [ticketUrl, setTicketUrl] = useState("");
  const [shopUrl, setShopUrl] = useState("");
  const [watchUrl, setWatchUrl] = useState("");

  // Construct the expected image URL
  const constructImageURL = useCallback(() => {
    const isOwn = calendar.handle === user?.handle;
    return `${process.env.REACT_APP_CLOUD_STORAGE_IMAGE_URL}/${
      calendar.handle
    }-${eventId}-eventImage.png?timestamp=${
      isOwn ? Date.now() : getMinuteTimestamp()
    }`;
  }, [calendar.handle, eventId, user?.handle]);

  const [eventImageUrl, setEventImageUrl] = useState(constructImageURL);

  useEffect(() => {
    setEventImageUrl(constructImageURL());
  }, [jCal, calendar.handle, eventId, constructImageURL]);

  // This function will be triggered when the image fails to load.
  const handleImageError = () => {
    setEventImageUrl(null);
  };

  const { readMore, setReadMore, formattedDescription, needsReadMore } =
    useFormatDescription({
      description: eventDescription,
      isEmbedded,
      setTicketUrl,
      setShopUrl,
      setWatchUrl,
    });

  const onSubscribeActionLocal = useCallback(
    (subscribed) => {
      // Optimistically update the UI to reflect the subscription status.
      if (
        onSubscribeAction &&
        setShowRecommendedCalendars &&
        setAddedCalendars
      ) {
        setShowRecommendedCalendars(false);
        setAddedCalendars((prev) => {
          if (subscribed) {
            return [...prev, { ...calendar, event }];
          }
          return prev.filter(
            (item) =>
              item.event?.jCal[1]?.find((prop) => prop[0] === "uid")[3] !==
              eventId
          );
        });
        if ((!subscribed && eventIdSubscribed === eventId) || subscribed) {
          onSubscribeAction(eventId, subscribed);
        }
      } else if (onSubscribeAction) {
        onSubscribeAction(eventId, subscribed);
      }
    },
    [
      onSubscribeAction,
      setShowRecommendedCalendars,
      setAddedCalendars,
      eventIdSubscribed,
      eventId,
      calendar,
      event,
    ]
  );

  return (
    <Component
      position={position}
      eventLinks={{
        ticketUrl,
        shopUrl,
        watchUrl,
      }}
      eventData={{
        eventId,
        eventSummary,
        eventLocation,
        eventDateStart,
        eventDateEnd,
        eventTvSeasonEpisode,
        formattedDescription,
        eventDescription,
        isPastEvent,
        isEventLive,
        isUpcoming: upcomingEventStatus.isUpcoming,
        upcomingText: upcomingEventStatus.upcomingText,
        displayMode: props.displayMode, // Pass displayMode to Event component
      }}
      eventUI={{
        homeTeamLogo,
        awayTeamLogo,
        background,
        eventImageUrl,
        handleImageError,
        setEventImageUrl,
        isSubscribed: eventIdSubscribed === eventId,
        readMore,
        setReadMore,
        needsReadMore,
      }}
      {...props}
      onSubscribeAction={onSubscribeActionLocal}
    />
  );
};

export const EventComposer = (Component) => {
  return (props) => {
    return <UIComponent Component={Component} {...props} />;
  };
};
